import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const initialVariant = { weight: '', price: '', stock: '' };

const Products = () => {
  const { token, user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    image: '',
    category: '',
    variants: [ { ...initialVariant } ]
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [variantModal, setVariantModal] = useState({ show: false, productId: null });
  const [variantForm, setVariantForm] = useState({ ...initialVariant });
  const [variantError, setVariantError] = useState('');
  const [variantLoading, setVariantLoading] = useState(false);
  const [editModal, setEditModal] = useState({ show: false, productId: null, variantIndex: null });
  const [editForm, setEditForm] = useState({ ...initialVariant });
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, productId: null, productName: '', input: '', error: '' });

  // Permission check
  const isSuperadmin = user && user.role === 'superadmin';

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line
  }, [token]);

  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const res = await fetch(`${apiBaseUrl}/api/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch products');
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleVariantChange = (idx, e) => {
    const newVariants = form.variants.map((v, i) =>
      i === idx ? { ...v, [e.target.name]: e.target.value } : v
    );
    setForm({ ...form, variants: newVariants });
  };

  const addVariant = () => {
    setForm({ ...form, variants: [ ...form.variants, { ...initialVariant } ] });
  };

  const removeVariant = (idx) => {
    setForm({ ...form, variants: form.variants.filter((_, i) => i !== idx) });
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const variants = form.variants.map(v => ({
        weight: v.weight,
        price: Number(v.price),
        stock: Number(v.stock)
      }));
      const res = await fetch(`${apiBaseUrl}/api/admin/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: form.name,
          description: form.description,
          image: form.image,
          category: form.category,
          variants
        })
      });
      if (!res.ok) throw new Error('Failed to create product');
      setShowModal(false);
      setForm({ name: '', description: '', image: '', category: '', variants: [ { ...initialVariant } ] });
      fetchProducts();
    } catch (err) {
      setFormError(err.message || 'Failed to create product');
    } finally {
      setFormLoading(false);
    }
  };

  // Add Variant to Existing Product
  const openVariantModal = (productId) => {
    setVariantModal({ show: true, productId });
    setVariantForm({ ...initialVariant });
    setVariantError('');
  };
  const closeVariantModal = () => {
    setVariantModal({ show: false, productId: null });
    setVariantForm({ ...initialVariant });
    setVariantError('');
  };
  const handleVariantFormChange = (e) => {
    setVariantForm({ ...variantForm, [e.target.name]: e.target.value });
  };
  const handleAddVariant = async (e) => {
    e.preventDefault();
    setVariantError('');
    setVariantLoading(true);
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const { weight, price, stock } = variantForm;
      if (!weight || price === '' || stock === '') throw new Error('All fields required');
      const product = products.find(p => p._id === variantModal.productId);
      if (!product) throw new Error('Product not found');
      const res = await fetch(`${apiBaseUrl}/api/admin/products/${product._id}/variant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ weight, price: Number(price), stock: Number(stock) })
      });
      if (!res.ok) throw new Error('Failed to add variant');
      closeVariantModal();
      fetchProducts();
    } catch (err) {
      setVariantError(err.message || 'Failed to add variant');
    } finally {
      setVariantLoading(false);
    }
  };

  // Edit Variant
  const openEditModal = (productId, variantIndex, variant) => {
    setEditModal({ show: true, productId, variantIndex });
    setEditForm({ ...variant });
    setEditError('');
  };
  const closeEditModal = () => {
    setEditModal({ show: false, productId: null, variantIndex: null });
    setEditForm({ ...initialVariant });
    setEditError('');
  };
  const handleEditFormChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };
  const handleEditVariant = async (e) => {
    e.preventDefault();
    setEditError('');
    setEditLoading(true);
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const { weight, price, stock } = editForm;
      const res = await fetch(`${apiBaseUrl}/api/admin/products/${editModal.productId}/variant/${editModal.variantIndex}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ weight, price: Number(price), stock: Number(stock) })
      });
      if (!res.ok) throw new Error('Failed to update variant');
      closeEditModal();
      fetchProducts();
    } catch (err) {
      setEditError(err.message || 'Failed to update variant');
    } finally {
      setEditLoading(false);
    }
  };

  // Delete Variant
  const handleDeleteVariant = async (productId, variantIndex) => {
    if (!window.confirm('Are you sure you want to delete this variant?')) return;
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const res = await fetch(`${apiBaseUrl}/api/admin/products/${productId}/variant/${variantIndex}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      if (!res.ok) throw new Error('Failed to delete variant');
      fetchProducts();
    } catch (err) {
      alert(err.message || 'Failed to delete variant');
    }
  };

  const openDeleteModal = (productId, productName) => {
    setDeleteModal({ show: true, productId, productName, input: '', error: '' });
  };
  const closeDeleteModal = () => {
    setDeleteModal({ show: false, productId: null, productName: '', input: '', error: '' });
  };
  const handleDeleteProduct = async () => {
    if (deleteModal.input !== deleteModal.productName) {
      setDeleteModal(dm => ({ ...dm, error: 'Product name does not match.' }));
      return;
    }
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const res = await fetch(`${apiBaseUrl}/api/admin/products/${deleteModal.productId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete product');
      closeDeleteModal();
      fetchProducts();
    } catch (err) {
      setDeleteModal(dm => ({ ...dm, error: err.message || 'Failed to delete product' }));
    }
  };

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-3xl font-bold mb-6 text-[#d4af37]">Product Management</h1>
      {!isSuperadmin && (
        <div className="mb-6 p-4 bg-yellow-100 text-yellow-800 rounded shadow text-center">
          You do not have permission to manage products. Please contact a superadmin.
        </div>
      )}
      {/* Show Create Product button for all, but disabled for normal admins */}
      <button
        className={`bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow mb-4${!isSuperadmin ? ' opacity-50 cursor-not-allowed' : ''}`}
        onClick={() => isSuperadmin && setShowModal(true)}
        disabled={!isSuperadmin}
      >
        Create Product
      </button>
      {/* Product Table/Grid */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded shadow">
          <thead>
            <tr>
              <th className="px-4 py-2 border">Image</th>
              <th className="px-4 py-2 border">Product Name</th>
              <th className="px-4 py-2 border">Category</th>
              <th className="px-4 py-2 border">Variants</th>
              <th className="px-4 py-2 border">Created</th>
              <th className="px-4 py-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <tr key={product._id}>
                <td className="px-4 py-2 border align-top">
                  <img src={product.image} alt={product.name} className="w-16 h-16 object-cover rounded" />
                </td>
                <td className="px-4 py-2 border align-top font-semibold">{product.name}</td>
                <td className="px-4 py-2 border align-top">{product.category}</td>
                <td className="px-4 py-2 border align-top">
                  {product.variants && product.variants.length > 0 ? (
                    <table className="w-full text-xs">
                      <thead>
                        <tr>
                          <th>Weight</th>
                          <th>Price</th>
                          <th>Stock</th>
                          <th>Edit</th>
                          <th>Delete</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.variants.map((v, idx) => (
                          <tr key={idx}>
                            <td>{v.weight}</td>
                            <td>${v.price.toFixed(2)}</td>
                            <td>
                              <span className={`px-2 py-1 rounded text-xs font-bold ${v.stock === 0 ? 'bg-red-400 text-white' : v.stock <= 5 ? 'bg-yellow-400 text-white' : 'bg-green-200 text-green-900'}`}>{v.stock}</span>
                            </td>
                            <td>
                              <button
                                onClick={() => isSuperadmin && openEditModal(product._id, idx, v)}
                                className={`bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs${!isSuperadmin ? ' opacity-50 cursor-not-allowed' : ''}`}
                                disabled={!isSuperadmin}
                              >
                                Edit
                              </button>
                            </td>
                            <td>
                              <button
                                onClick={() => isSuperadmin && handleDeleteVariant(product._id, idx)}
                                className={`bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs${!isSuperadmin ? ' opacity-50 cursor-not-allowed' : ''}`}
                                disabled={!isSuperadmin}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <span>No variants</span>
                  )}
                </td>
                <td className="px-4 py-2 border align-top">{product.createdAt ? new Date(product.createdAt).toLocaleDateString() : ''}</td>
                <td className="px-4 py-2 border align-top">
                  <button
                    onClick={() => isSuperadmin && openVariantModal(product._id)}
                    className={`bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs mt-2 w-full${!isSuperadmin ? ' opacity-50 cursor-not-allowed' : ''}`}
                    disabled={!isSuperadmin}
                  >
                    + Add Variant
                  </button>
                  <button
                    onClick={() => isSuperadmin && openDeleteModal(product._id, product.name)}
                    className={`bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-xs mt-2 w-full${!isSuperadmin ? ' opacity-50 cursor-not-allowed' : ''}`}
                    disabled={!isSuperadmin}
                  >
                    Delete Product
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Modals and forms for superadmin only */}
      {isSuperadmin && (
        <>
          {/* Create Product Modal */}
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-lg relative">
                <button onClick={() => setShowModal(false)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl">&times;</button>
                <h3 className="text-xl font-bold mb-4">Create Product</h3>
                <form onSubmit={handleCreateProduct} className="flex flex-col gap-3">
                  <input name="name" value={form.name} onChange={handleFormChange} required placeholder="Product Name" className="border rounded px-3 py-2" />
                  <input name="description" value={form.description} onChange={handleFormChange} placeholder="Description" className="border rounded px-3 py-2" />
                  <input name="image" value={form.image} onChange={handleFormChange} placeholder="Image URL" className="border rounded px-3 py-2" />
                  <input name="category" value={form.category} onChange={handleFormChange} required placeholder="Category" className="border rounded px-3 py-2" />
                  <div className="flex flex-col gap-2">
                    <label className="font-semibold">Variants</label>
                    {form.variants.map((v, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <input name="weight" value={v.weight} onChange={e => handleVariantChange(idx, e)} required placeholder="Weight" className="border rounded px-2 py-1 w-20" />
                        <input name="price" type="number" value={v.price} onChange={e => handleVariantChange(idx, e)} required placeholder="Price" className="border rounded px-2 py-1 w-20" />
                        <input name="stock" type="number" value={v.stock} onChange={e => handleVariantChange(idx, e)} required placeholder="Stock" className="border rounded px-2 py-1 w-20" />
                        {form.variants.length > 1 && (
                          <button type="button" onClick={() => removeVariant(idx)} className="text-red-500 hover:text-red-700 text-lg">&times;</button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={addVariant} className="text-blue-600 hover:underline text-sm mt-1">+ Add Variant</button>
                  </div>
                  {formError && <div className="text-red-500 text-sm">{formError}</div>}
                  <button type="submit" disabled={!isSuperadmin || formLoading} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow mt-2">{formLoading ? 'Creating...' : 'Create Product'}</button>
                </form>
              </div>
            </div>
          )}
          {/* Add Variant Modal */}
          {variantModal.show && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative">
                <button onClick={closeVariantModal} className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl">&times;</button>
                <h3 className="text-xl font-bold mb-4">Add Variant</h3>
                <form onSubmit={handleAddVariant} className="flex flex-col gap-3">
                  <input name="weight" value={variantForm.weight} onChange={handleVariantFormChange} required placeholder="Weight" className="border rounded px-3 py-2" />
                  <input name="price" type="number" value={variantForm.price} onChange={handleVariantFormChange} required placeholder="Price" className="border rounded px-3 py-2" />
                  <input name="stock" type="number" value={variantForm.stock} onChange={handleVariantFormChange} required placeholder="Stock" className="border rounded px-3 py-2" />
                  {variantError && <div className="text-red-500 text-sm">{variantError}</div>}
                  <button type="submit" disabled={!isSuperadmin || variantLoading} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow mt-2">{variantLoading ? 'Adding...' : 'Add Variant'}</button>
                </form>
              </div>
            </div>
          )}
          {/* Edit Variant Modal */}
          {editModal.show && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative">
                <button onClick={closeEditModal} className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl">&times;</button>
                <h3 className="text-xl font-bold mb-4">Edit Variant</h3>
                <form onSubmit={handleEditVariant} className="flex flex-col gap-3">
                  <input name="weight" value={editForm.weight} onChange={handleEditFormChange} required placeholder="Weight" className="border rounded px-3 py-2" />
                  <input name="price" type="number" value={editForm.price} onChange={handleEditFormChange} required placeholder="Price" className="border rounded px-3 py-2" />
                  <input name="stock" type="number" value={editForm.stock} onChange={handleEditFormChange} required placeholder="Stock" className="border rounded px-3 py-2" />
                  {editError && <div className="text-red-500 text-sm">{editError}</div>}
                  <button type="submit" disabled={!isSuperadmin || editLoading} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow mt-2">{editLoading ? 'Saving...' : 'Save Changes'}</button>
                </form>
              </div>
            </div>
          )}
          {deleteModal.show && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative">
                <button onClick={closeDeleteModal} className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl">&times;</button>
                <h3 className="text-xl font-bold mb-4 text-red-600">Delete Product</h3>
                <p className="mb-2">Are you sure you want to delete the product <span className="font-bold">{deleteModal.productName}</span>?</p>
                <p className="mb-2 text-sm text-gray-600">Type the product name exactly to confirm deletion:</p>
                <input
                  type="text"
                  className="border rounded px-3 py-2 w-full mb-2"
                  value={deleteModal.input}
                  onChange={e => setDeleteModal(dm => ({ ...dm, input: e.target.value, error: '' }))}
                  placeholder="Product name"
                />
                {deleteModal.error && <div className="text-red-500 text-sm mb-2">{deleteModal.error}</div>}
                <button
                  onClick={handleDeleteProduct}
                  className={`bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded shadow w-full ${deleteModal.input !== deleteModal.productName ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={!isSuperadmin || deleteModal.input !== deleteModal.productName}
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Products; 